// O desenho de cada cosmético (lote 3, item 15), só com os tokens do design system: a mesa (feltro, borda e friso),
// o verso das cartas e a moldura do rosto. O catálogo e as regras ficam em @truco6/gamificacao; aqui, como cada
// visual aparece na mesa, na Coleção e nas prévias.
import type { CSSProperties } from 'react';
import { PADRAO_DA_CATEGORIA } from '@truco6/gamificacao';
import { cores, type OpcoesVerso } from '@truco6/ui';

interface VisualDeMesa { feltro: string; feltroEscuro: string; feltroClaro: string; borda: string; bordaEscura: string; friso: string }
const mistura = (cor: string, pct: number, com = 'var(--ground-900)') => `color-mix(in srgb, ${cor} ${pct}%, ${com})`;

const MESAS: Record<string, VisualDeMesa> = {
  mesa_feltro_classico: {
    feltro: 'var(--felt-500)', feltroEscuro: 'var(--felt-700)', feltroClaro: mistura('var(--felt-500)', 85, 'var(--cream-050)'),
    borda: 'var(--wood-500)', bordaEscura: 'var(--wood-700)', friso: 'var(--gold-600)',
  },
  mesa_boteco_azul: {
    feltro: mistura('var(--nos-400)', 40), feltroEscuro: mistura('var(--nos-400)', 22), feltroClaro: mistura('var(--nos-400)', 55),
    borda: 'var(--wood-500)', bordaEscura: 'var(--wood-700)', friso: 'var(--cream-050)',
  },
  mesa_couro_vinho: {
    feltro: mistura('var(--eles-400)', 42, 'var(--ink-900)'), feltroEscuro: mistura('var(--eles-400)', 26, 'var(--ink-900)'),
    feltroClaro: mistura('var(--eles-400)', 55, 'var(--ink-900)'), borda: 'var(--wood-700)', bordaEscura: 'var(--ink-900)', friso: 'var(--gold-600)',
  },
  mesa_de_ouro: {
    feltro: mistura('var(--felt-500)', 75), feltroEscuro: 'var(--ground-800)', feltroClaro: 'var(--felt-500)',
    borda: 'var(--gold-600)', bordaEscura: 'var(--wood-700)', friso: 'var(--gold-400)',
  },
  mesa_fogueira: {
    feltro: mistura('var(--fire-700)', 45), feltroEscuro: mistura('var(--fire-700)', 25), feltroClaro: mistura('var(--fire-500)', 55),
    borda: 'var(--wood-500)', bordaEscura: 'var(--wood-700)', friso: 'var(--fire-500)',
  },
};

// As variáveis que a mesa usa (Mesa.tsx e estilos.css): sem cosmético, as da casa.
export function estiloDaMesa(id: string | null | undefined): CSSProperties {
  const m = MESAS[id ?? ''] ?? MESAS[PADRAO_DA_CATEGORIA.mesa]!;
  return {
    '--mesa-feltro': m.feltro, '--mesa-feltro-escuro': m.feltroEscuro, '--mesa-feltro-claro': m.feltroClaro,
    '--mesa-borda': m.borda, '--mesa-borda-escura': m.bordaEscura, '--mesa-friso': m.friso,
  } as CSSProperties;
}

// O verso da casa tem o 6 da marca no centro; os versos da coleção seguem com o monograma T6.
const VERSOS: Record<string, OpcoesVerso> = {
  verso_casa: {},
  verso_azulejo: { base: cores.cream050, line: cores.felt500, padrao: 'azulejo', mono: 'T6', monoFill: cores.felt500, monoInk: cores.cream050 },
  verso_dourado: { base: cores.gold600, line: cores.gold400, padrao: 'losango', mono: 'T6', monoFill: cores.ink900, monoInk: cores.gold400 },
  verso_grao: { base: cores.ground800, line: cores.gold400, padrao: 'pontos', mono: 'T6', monoFill: cores.gold400, monoInk: cores.ground900 },
  verso_vulcao: { base: cores.wood700, line: cores.fire500, padrao: 'listras', mono: 'T6', monoFill: cores.fire500, monoInk: cores.cream050 },
  verso_fogueira: { base: cores.ground900, line: cores.fire500, padrao: 'losango', mono: 'T6', monoFill: cores.gold400, monoInk: cores.ground900 },
};
export const versoDoCosmetico = (id: string | null | undefined): OpcoesVerso => VERSOS[id ?? ''] ?? {};

interface VisualDeMoldura { cor: string; brilho?: string; dupla?: boolean }
const MOLDURAS: Record<string, VisualDeMoldura> = {
  moldura_madeira: { cor: 'var(--wood-500)' },
  moldura_bronze: { cor: 'var(--bronze-400)' },
  moldura_prata: { cor: 'var(--prata-300)' },
  moldura_creme: { cor: 'var(--cream-050)' },
  moldura_feltro: { cor: 'var(--felt-500)' },
  moldura_dourada: { cor: 'var(--gold-400)', dupla: true },
  moldura_chama: { cor: 'var(--fire-500)', brilho: 'var(--fire-500)' },
  moldura_brasa: { cor: 'var(--fire-700)', dupla: true },
  moldura_fogueira: { cor: 'var(--gold-400)', brilho: 'var(--fire-500)', dupla: true },
};

// A moldura fica por fora da borda do trio (NÓS ou ELES), que nunca muda.
export function sombraDaMoldura(id: string | null | undefined): string | undefined {
  const m = id ? MOLDURAS[id] : undefined;
  if (!m) return undefined;
  const aneis = [`0 0 0 2px var(--ground-900)`, `0 0 0 5px ${m.cor}`];
  if (m.dupla) aneis.push(`0 0 0 7px var(--ground-900)`, `0 0 0 8px ${m.cor}`);
  if (m.brilho) aneis.push(`0 0 14px 3px ${m.brilho}`);
  return aneis.join(', ');
}

// Prévia da mesa na Coleção: o hexágono com a borda, o friso e o feltro.
export function PreviaDaMesa({ id, tamanho = 72 }: { id: string; tamanho?: number }) {
  const h = Math.round(tamanho * 1.12);
  return (
    <div className="previa-mesa" style={{ ...estiloDaMesa(id), width: tamanho, height: h }} aria-hidden="true">
      <div className="hex" style={{ inset: 0, background: 'var(--mesa-borda-escura)' }} />
      <div className="hex" style={{ inset: 3, background: 'var(--mesa-borda)' }} />
      <div className="hex" style={{ inset: 9, background: 'var(--mesa-friso)' }} />
      <div className="hex felt" style={{ inset: 11 }} />
    </div>
  );
}
